/* tslint:disable: no-duplicate-imports */
import { format,getPayloadObject, getTelemetryAttributes, IPayLoad, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import { IProductRefinerHierarchy } from '@msdyn365-commerce/commerce-entities';
import { RatingComponent } from '@msdyn365-commerce/components';
import MsDyn365, { IAny, ICoreContext, IGeneric } from '@msdyn365-commerce/core';
import { ProductRefinerSource, ProductRefinerValue, RefinerType } from '@msdyn365-commerce/retail-proxy';
import * as React from 'react';
import { getVirtualRefinerNames, IVirtualRefinersConfig, regExSpecialChars } from '../../../Utilities/plp-url-utils';
import { IRefineItemURLParts } from '../mfrm-search-result-container';
import { IRefineItemToggleNotification } from './refine-item-toggle-notification';
import { IRefineItemCommonProps } from './refine-item.props.common';

/**
 * RefineItem properties
 */
export interface IRefineItemProps {
    parentProductRefinerHierarchy: IProductRefinerHierarchy;
    productRefinerValue: ProductRefinerValue;
    selectedRefinementCriterion: ProductRefinerValue | undefined;
    selectedRefinerValues?: ProductRefinerValue[];
    refineItemCommonProps: IRefineItemCommonProps;
    isDisabled: boolean;
    context: ICoreContext<IGeneric<IAny>>;
    moduleId: string;
    moduleTypeName: string;
    index?: number;
    /** The telemetry content */
    telemetryContent?: ITelemetryContent;
    isMattressesCategory: boolean | undefined;
    urlRefinerParts: string[];
    urlParts: IRefineItemURLParts;
    sortedConfigValues?: IVirtualRefinersConfig[] | undefined;
    filteredRefiners?: IProductRefinerHierarchy[] | undefined;
    onToggle(notfication: Readonly<IRefineItemToggleNotification>): void;
    urlBuilder(refiner: IRefineItemToggleNotification): string;
    singleProductCountAriaLabel?: string;
    refineItemAriaLabel?: string;
    refineItemsAriaLabel?: string;
    isMobileView?: boolean;
}

/**
 * Refine item state
 */
export interface IRefineItemState extends React.ComponentState {
    isChecked: boolean;
    renderingError?: object;
}

/**
 * Single-select and multi-select refine item component (controlled by RefineSubmenu)
 */
export default class RefineItem extends React.Component<IRefineItemProps, IRefineItemState> {
    private anchorType: React.RefObject<HTMLAnchorElement>;
    private payLoad: IPayLoad;

    constructor(props: IRefineItemProps) {
        super(props);
        this._onClick = this._onClick.bind(this);
        this.state = {
            isChecked: !!this.props.selectedRefinementCriterion
        };
        this.anchorType = React.createRef();
        this.payLoad = getPayloadObject('click', this.props.telemetryContent!, '');
    }

    // tslint:disable-next-line:max-func-body-length
    public render(): JSX.Element | undefined {
        const {
            isDisabled,
            refineItemCommonProps,
            parentProductRefinerHierarchy,
            productRefinerValue,
            selectedRefinementCriterion,
            singleProductCountAriaLabel,
            refineItemAriaLabel,
            refineItemsAriaLabel,
            index,
            children,
            onToggle,
            context,
            telemetryContent,
            ...attrs
        } = this.props;
        if (!productRefinerValue) {
            refineItemCommonProps.telemetry.warning('[refine-item] Cannot render refineItem without productRefinerValue');
            return undefined;
        }
        if (!productRefinerValue.LeftValueBoundString) {
            refineItemCommonProps.telemetry.warning(
                `[refine-item]  RefineItem without LeftValueBoundString: ${JSON.stringify(productRefinerValue)}`
            );
        }
        const isSingleSelect = parentProductRefinerHierarchy.RefinerTypeValue === RefinerType?.SingleSelect;
        let itemTypeClassName = isSingleSelect ? 'single-select' : 'multi-select';
        itemTypeClassName = `ms-refine-submenu-item ${itemTypeClassName}`;
        const inputType = isSingleSelect ? 'radio' : 'checkbox';
        const isChecked = !!selectedRefinementCriterion;
        itemTypeClassName = isChecked ? `${itemTypeClassName}-checked` : itemTypeClassName;
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- Replace with actual value for empty string.
          const productText = productRefinerValue.LeftValueBoundLocalizedString || productRefinerValue.LeftValueBoundString || '';
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- Replace with actual value for empty string.
          const productCount = productRefinerValue.Count || 0;
          let refineMenuItemAriaLabel;
          if (productCount === 1) {
              refineMenuItemAriaLabel = refineItemAriaLabel ? format(refineItemAriaLabel, parentProductRefinerHierarchy.KeyName, productText) : '';
          } else {
              refineMenuItemAriaLabel =
                  refineItemsAriaLabel ? format(refineItemsAriaLabel, parentProductRefinerHierarchy.KeyName, productText, productCount) : '';
          }
        if (parentProductRefinerHierarchy.SourceValue === ProductRefinerSource?.Rating) {
            if (productRefinerValue.LeftValueBoundString) {
                this.payLoad.contentAction.etext = productRefinerValue.LeftValueBoundLocalizedString;
                const attribute = getTelemetryAttributes(telemetryContent!, this.payLoad);
                const refinerRating = parseInt(productRefinerValue.LeftValueBoundString, 10);
                const ratingRefiner = this.props.selectedRefinerValues?.find(
                    value => value.DataTypeValue === productRefinerValue.DataTypeValue
                );
                const selectedRating = parseInt(ratingRefiner?.LeftValueBoundString || '0', 10);
                let ratingComponentClass: string = 'ms-rating-component';
                if (refinerRating > selectedRating) {
                    ratingComponentClass = `${ratingComponentClass}__up`;
                } else if (refinerRating < selectedRating) {
                    ratingComponentClass = `${ratingComponentClass}__down`;
                } else {
                    ratingComponentClass = `${ratingComponentClass}__current`;
                }
                return (
                    <li
                        className='ms-refine-submenu-item'
                        role={!this.props.isMobileView ? 'presentation' : undefined}
                        id={`${parentProductRefinerHierarchy.KeyName!}_${index}`}
                    >
                        <a
                            href={this._getRefinerUrl()}
                            aria-label={`${parentProductRefinerHierarchy.KeyName!}_${productRefinerValue.LeftValueBoundLocalizedString!}`}
                            aria-selected={isChecked}
                            onClick={this._onClick}
                            {...attribute}
                        >
                            <RatingComponent
                                className={ratingComponentClass}
                                avgRating={refinerRating}
                                ratingCount={productRefinerValue.LeftValueBoundLocalizedString ?? productRefinerValue.LeftValueBoundString}
                                hideCount={false}
                                readOnly={true}
                                ariaLabel=''
                                context={context}
                                id={this.props.moduleId}
                                typeName={this.props.moduleTypeName}
                                data={{}}
                            />
                            <span className='refine-submenu-item__rating' aria-hidden={this.props.isMobileView ? 'true' : undefined}>
                                {productRefinerValue.Count !== undefined && `(${productRefinerValue.Count})`}
                            </span>
                        </a>
                    </li>
                );
            } else {
                return undefined;
            }
        } else {
            this.payLoad.contentAction.etext =
                productRefinerValue.LeftValueBoundLocalizedString ?? productRefinerValue.LeftValueBoundString;
            const attribute = getTelemetryAttributes(telemetryContent!, this.payLoad);
            return (
                <li className='ms-refine-submenu-item' id={`${parentProductRefinerHierarchy.KeyName!}_${index}`}>
                    <a
                        key={!!selectedRefinementCriterion ? 'true' : 'false'}
                        ref={this.anchorType}
                        href={this._getRefinerUrl()}
                        tabIndex={0}
                        onClick={this._onClick}
                        className={itemTypeClassName}
                        role={inputType}
                        aria-checked={!!selectedRefinementCriterion}
                        aria-label={refineMenuItemAriaLabel}
                        {...attribute}
                        {...attrs}
                    >
                        <span className='ms-refine-submenu-item__label' data-cy={`${productRefinerValue && productRefinerValue.LeftValueBoundString}`}>
                            {productRefinerValue.LeftValueBoundLocalizedString || productRefinerValue.LeftValueBoundString}
                            {productRefinerValue.Count !== undefined && ` (${productRefinerValue.Count})`}
                        </span>
                    </a>
                </li>
            );
        }
    }

    private _getIsCategoryPage(): boolean {
        const { context } = this.props;
        const requestContext = context && context.request;
        const pageType =
            requestContext.urlTokens.pageType === 'Category' || (requestContext.query && requestContext.query.categoryId)
                ? 'Category'
                : 'Search';
        return pageType?.toLowerCase() === 'search' ? false : true;
    }

    private _getRefinerUrl(): string {
        const { urlBuilder, parentProductRefinerHierarchy, productRefinerValue, selectedRefinementCriterion, urlRefinerParts, urlParts, sortedConfigValues, filteredRefiners } = this.props;
        const urlSpecialCharsConfig = this.props.context.app.config.canonicalUrlspecialCharIncludeList;
        const isCategoryPage: boolean = this._getIsCategoryPage();
        if (productRefinerValue && parentProductRefinerHierarchy.KeyName) {
            const parentProductRefinerName = parentProductRefinerHierarchy.KeyName.toLowerCase().trim();
            const virturalRefinerConfigName = getVirtualRefinerNames(this.props.context.request, this.props.isMattressesCategory);
            if (virturalRefinerConfigName.indexOf(parentProductRefinerName) !== -1 && isCategoryPage) {
                const refinerParts = [...urlRefinerParts];
                const currentRefinerName = productRefinerValue.LeftValueBoundString && productRefinerValue.LeftValueBoundString.toLowerCase().trim().split(' ').join('-')?.replace(/\&/g, 'and')?.replace(regExSpecialChars(urlSpecialCharsConfig), '-');
                if (currentRefinerName) {
                    if (refinerParts.indexOf(currentRefinerName) !== -1) {
                        const index = refinerParts.indexOf(currentRefinerName);
                        refinerParts.splice(index, 1);
                    } else if (currentRefinerName !== '' && refinerParts.indexOf(currentRefinerName) === -1) {
                        refinerParts.push(currentRefinerName);
                    }
                }

                // setup object with refiner values
                const sortedValues: { [key: string]: string[] } = {};
                filteredRefiners && filteredRefiners?.forEach((refinerSet) => {
                    let refinerName = refinerSet.KeyName;
                    if (refinerName) {
                        refinerName = refinerName.toLowerCase();
                        sortedValues[refinerName] = []; // size, brand, price
                    }
                    refinerSet.Values.forEach((refiner) => {
                        refinerParts.forEach((refinerPart) => {
                            if (refinerName && refiner.LeftValueBoundString &&
                                (refiner.LeftValueBoundString.toLowerCase()?.replace(/\&/g, 'and')?.replace(regExSpecialChars(urlSpecialCharsConfig), '-') === refinerPart.toLocaleLowerCase().split('-').join(' ')
                                    || refiner.LeftValueBoundString.toLowerCase()?.replace(/\&/g, 'and')?.replace(regExSpecialChars(urlSpecialCharsConfig), '-') === refinerPart.toLocaleLowerCase())) {
                                sortedValues[refinerName].push(refiner.LeftValueBoundString.toLowerCase().split(' ').join('-'));
                            } else if (refinerName && refiner.LeftValueBoundString &&
                                refiner.LeftValueBoundString.toLowerCase() === refinerPart.toLocaleLowerCase().split('-').join(' ')) {
                                sortedValues[refinerName].push(refinerPart);
                            }
                        });
                    });
                });
                // aplpha sort
                // @ts-ignore
                for (const sortedValue of Object.keys(sortedValues)) {
                    sortedValues[sortedValue].length > 0 && sortedValues[sortedValue].sort((a: string, b: string) => {
                        if (a < b) { return -1; }
                        if (a > b) { return 1; }
                        return 0;
                    });
                }
                // @ts-ignore
                const finalRefiners: string[] = [];
                sortedConfigValues && sortedConfigValues.forEach((sortedConfigValue) => {
                    const sortedRefinerName = sortedConfigValue.refinerName.toLowerCase();
                    if (sortedValues[sortedRefinerName] !== undefined && sortedValues[sortedRefinerName].length > 0) {
                        let concatValue: string | string[] = sortedValues[sortedRefinerName];
                        if (sortedValues[sortedRefinerName].length > 1) {
                            concatValue = sortedValues[sortedRefinerName].join('|');
                        } else if (sortedValues[sortedRefinerName].length === 1) {
                            concatValue = sortedValues[sortedRefinerName][0];
                        }
                        // @ts-ignore
                        finalRefiners.push(concatValue.split(' ').join('-')?.replace(/\&/g, 'and')?.replace(regExSpecialChars(urlSpecialCharsConfig), '-'));
                    }
                });
                const queryStr = this.props.context.request?.url?.requestUrl?.search;
                return `${urlParts.origin}/${urlParts.categoryPart}${finalRefiners && finalRefiners.length > 0 ? `/${finalRefiners.join('\/')}` : ''}/${urlParts.categoryId ? urlParts.categoryId : ''}${queryStr || ''}`;
            } else if (productRefinerValue) {
                return urlBuilder({
                    parentProductRefinerHierarchy: parentProductRefinerHierarchy,
                    productRefinerValue: productRefinerValue,
                    isSelecting: !selectedRefinementCriterion
                });
            }
        }

        return '';
    }

    private _onClick = (e: React.MouseEvent<HTMLAnchorElement | HTMLInputElement>): void => {
        e.preventDefault();

        const { parentProductRefinerHierarchy, productRefinerValue, selectedRefinementCriterion } = this.props;
        if (productRefinerValue) {
            this.props.onToggle({
                parentProductRefinerHierarchy: parentProductRefinerHierarchy,
                productRefinerValue: productRefinerValue,
                isSelecting: !selectedRefinementCriterion
            });

            MsDyn365.isBrowser &&
                setTimeout(() => {
                    this.anchorType.current && this.anchorType.current.focus();
                }, 0);
        }
    };
}
